import React, { FC, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { TableBody, TableFooter } from './ParticipantsTable.styles';
import PageSection from '../../HOC/PageSection/PageSection';
import { useAccordion } from '../../hooks/useAccordion';
import { IPagingInfo, IParticipantsItem } from '../../services/surveys';
import GoToPage from '../GoToPage/GoToPage';
import NoData from '../NoData/NoData';
import Pagination from '../Pagination/Pagination';
import RowsPerPage from '../RowsPerPage/RowsPerPage';
import ParticipantTableHeader from './ParticipantsTableHeader/ParticipantTableHeader';
import ParticipantsTableTile from './ParticipantsTableTile/ParticipantsTableTile';

interface IParticipationsTable {
  items: IParticipantsItem[];
  pagingInfo: IPagingInfo;
  applyFilters: (filters: Record<string, string | null | undefined>) => void;
  selectedLanguages: string[];
  deleteItem: (id: string | null) => void;
  editItem: (item: IParticipantsItem) => void;
  surveyType: string;
}

const ParticipantsTable: FC<IParticipationsTable> = ({
  items,
  pagingInfo,
  applyFilters,
  selectedLanguages,
  deleteItem,
  editItem,
  surveyType,
}) => {
  const { t } = useTranslation();
  const isEffectSurvey = surveyType === 'EFFECT';
  const { pageNo, totalPageCount, itemsPerPage } = pagingInfo;
  const { itemsArr, setItemStatus } = useAccordion(items);

  const renderTableBody = useMemo(() => {
    return itemsArr.map(item => (
      <ParticipantsTableTile
        key={item.id}
        item={item}
        setItemStatus={setItemStatus}
        deleteItem={deleteItem}
        editItem={editItem}
        isEffectSurvey={isEffectSurvey}
      />
    ));
  }, [itemsArr, deleteItem, editItem, setItemStatus]);

  return (
    <PageSection>
      <>
        <ParticipantTableHeader
          applyFilters={applyFilters}
          selectedLanguages={selectedLanguages}
          isEffectSurvey={isEffectSurvey}
        />
        <TableBody id="TableBody">
          {itemsArr.length > 0 ? (
            renderTableBody
          ) : (
            <NoData message={t('viewSurvey.participants.listHead.noParticipantsInfo')} />
          )}
        </TableBody>
        <TableFooter id="TableFooter">
          <RowsPerPage
            itemsPerPage={Number(itemsPerPage)}
            pagesCount={Number(totalPageCount)}
            menu={['7', '10', '25', '50', '75', '100']}
          />
          <Pagination
            activePage={Number(pageNo)}
            totalPageCount={Number(totalPageCount)}
            applyFilters={(pageNo: number) => applyFilters({ pageNo: String(pageNo) })}
          />
          <GoToPage applyFilters={applyFilters} pageLimit={totalPageCount} />
        </TableFooter>
      </>
    </PageSection>
  );
};

export default ParticipantsTable;
